import { useEffect, useRef, useState } from 'react';

interface UseHighlightNewItemOptions {
  /**
   * Duration of the highlight animation in milliseconds
   * @default 2000
   */
  duration?: number;
  /**
   * Scroll behavior when highlighting new item
   * @default 'smooth'
   */
  scrollBehavior?: ScrollBehavior;
  /**
   * Block position for scrollIntoView
   * @default 'center'
   */
  scrollBlock?: ScrollLogicalPosition;
}

/**
 * A reusable hook that handles highlighting newly added items with a blinking border animation
 * and automatically scrolling to them.
 *
 * @param options - Configuration options for the highlight behavior
 * @returns Object containing refs array, highlighted index, and trigger function
 *
 * @example
 * ```tsx
 * const { itemRefs, highlightedIndex, highlightItem } = useHighlightNewItem();
 *
 * const handleAddItem = () => {
 *   const newIndex = items.length;
 *   addItem();
 *   highlightItem(newIndex);
 * };
 *
 * return (
 *   <div>
 *     {items.map((item, index) => (
 *       <div
 *         key={index}
 *         ref={(el) => { itemRefs.current[index] = el; }}
 *         className={highlightedIndex === index ? 'animate-border-blink' : ''}
 *       >
 *         {item}
 *       </div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useHighlightNewItem<T extends HTMLElement = HTMLDivElement>(
  options: UseHighlightNewItemOptions = {},
) {
  const {
    duration = 1000,
    scrollBehavior = 'smooth',
    scrollBlock = 'center',
  } = options;

  // keyed refs, usable as itemRefs.current['customer-0'] = el
  const itemRefs = useRef<Record<string, T | null>>({});
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  /**
   * Highlights the item at the specified key by scrolling to it
   * and applying the animation class
   */
  const highlightItem = (key: string) => {
    setHighlightedKey(key);
  };

  /**
   * Manually clear the highlight
   */
  const clearHighlight = () => {
    setHighlightedKey(null);
  };

  // Effect to handle scroll and animation cleanup
  useEffect(() => {
    if (highlightedKey !== null && itemRefs.current[highlightedKey]) {
      const element = itemRefs.current[highlightedKey];

      // Scroll to the highlighted item
      element?.scrollIntoView({
        behavior: scrollBehavior,
        block: scrollBlock,
      });

      // Clear the highlight after the specified duration
      const timer = setTimeout(() => {
        setHighlightedKey(null);
      }, duration);

      return () => clearTimeout(timer);
    }
    return;
  }, [highlightedKey, duration, scrollBehavior, scrollBlock]);

  return {
    itemRefs,
    highlightedKey,
    highlightItem,
    clearHighlight,
  };
}
